import type { ElementWithChildren, SlotsConfig } from '@magicschool/spellbook/lib/slots';
import { createWrappedContent, defaultRenderContent, processSlots } from '@magicschool/spellbook/lib/slots';
import type { ReactNode } from 'react';
import { Children, cloneElement, isValidElement, useMemo } from 'react';

type UseChildrenSlotsResult = {
  renderedChildren: ReactNode;
};

type UseChildrenSlotsOptions<S extends SlotsConfig> = {
  slots?: S;
  renderContent?: (props: { rest: ReactNode; nonSlotChildren: ReactNode[]; slots: Record<string, ReactNode> }) => ReactNode;
};

export function useRenderedChildren<S extends SlotsConfig>(
  children: ReactNode,
  options: UseChildrenSlotsOptions<S>,
): UseChildrenSlotsResult {
  const renderedChildren = useMemo(() => {
    const hasSlots = Object.keys(options.slots || {}).length > 0;

    if (!isValidElement(children)) {
      return children;
    }

    const childElement = children as ElementWithChildren;

    if (!childElement.props) {
      return childElement;
    }

    let content: ReactNode = null;

    if (hasSlots) {
      const { slots, nonSlotChildren } = processSlots(childElement.props.children, options.slots!);
      content = options?.renderContent
        ? options.renderContent({
            slots,
            rest: createWrappedContent(nonSlotChildren),
            nonSlotChildren,
          })
        : defaultRenderContent(slots, createWrappedContent(nonSlotChildren));
    } else {
      const nonSlotChildren = Children.toArray(childElement.props.children);
      const wrappedContent = createWrappedContent(nonSlotChildren);

      content = options?.renderContent
        ? options.renderContent({
            slots: {},
            rest: wrappedContent,
            nonSlotChildren,
          })
        : wrappedContent;
    }

    return cloneElement(childElement, undefined, content);
  }, [children, options]);

  return { renderedChildren };
}
